import type WaSelect from "@web.awesome.me/webawesome-pro/dist/components/select/select.js";
import hostStyles from "@web.awesome.me/webawesome-pro/dist/styles/component/host.styles.js";
import { isEmptyObject } from "es-toolkit";
import { css, html, type PropertyValues } from "lit";
import { live } from "lit/directives/live.js";
import { z } from "zod";
import { checkNotNil } from "../../check-not-nil.js";
import { FormAssociationMixin } from "../../mixins/form-associated-element.js";
import { attributeProperty } from "../../property.js";
import type { ParserType } from "../../schemas/parser.js";
import { WmElement } from "../element.js";

// TODO: proper types with enum schemas and shit.
// TODO: custom errors support.
type Kind = ParserType<typeof Kind>;
const Kind = attributeProperty("kind", z.string());

type DefaultUnit = ParserType<typeof DefaultUnit>;
const DefaultUnit = attributeProperty("default-unit", z.string());

export class WmMetricDefinition extends FormAssociationMixin(WmElement) {
  static readonly TAG_NAME = "wm-metric-definition";

  static properties = {
    ...super.properties,
    kind: Kind,
    defaultUnit: DefaultUnit,
  };

  static styles = [
    hostStyles,
    css`
      fieldset {
        padding: var(--wa-space-m);
        display: flex;
        flex-direction: column;
        margin-block: 0;
        margin-inline: 0;
        gap: var(--wa-space-m);
        align-items: stretch;
        border: solid 1px var(--wa-form-control-border-color);
        border-radius: var(--wa-form-control-border-radius);
      }
    `,
  ];

  declare kind: Kind;
  declare defaultUnit: DefaultUnit;

  constructor() {
    super();
    this.name = "definition";
    this.kind = undefined;
    this.defaultUnit = undefined;
    this.internals.setFormValue(null);
  }

  protected override render(): unknown {
    return html`
      <fieldset>
        <legend>Definition</legend>
        <wa-select
          label="Kind"
          name="kind"
          required
          with-clear
          .value=${live(this.kind)}
          @input=${this.#handleKindInput}>
          <wa-option value="Mass">Mass</wa-option>
        </wa-select>
        <wa-select
          label="Default Unit"
          name="default_unit"
          required
          with-clear
          .value=${live(this.defaultUnit)}
          @input=${this.#handleDefaultUnitInput}>
          <wa-option value="Pounds">Pounds</wa-option>
        </wa-select>
      </fieldset>
    `;
  }

  protected override updated(_: PropertyValues): void {
    this.#updateFormValue();

    const kindInput = this.#queryKindInput();
    const defaultUnitInput = this.#queryDefaultUnitInput();
    Promise.all([
      kindInput.updateComplete,
      defaultUnitInput.updateComplete,
    ]).then(() => {
      this.#updateFormValidity();
    });
  }

  #handleKindInput(e: InputEvent) {
    const target = checkNotNil(e.target) as WaSelect;
    this.kind = target.value as string | null;
  }

  #handleDefaultUnitInput(e: InputEvent) {
    const target = checkNotNil(e.target) as WaSelect;
    this.defaultUnit = target.value as string | null;
  }

  #queryKindInput(): WaSelect {
    return this.queryNotNil(`wa-select[name="kind"]`);
  }

  #queryDefaultUnitInput(): WaSelect {
    return this.queryNotNil(`wa-select[name="default_unit"]`);
  }

  #updateFormValue() {
    const json: Record<string, unknown> = {};
    if (this.kind) {
      json.kind = this.kind;
    }

    if (this.defaultUnit) {
      json.defaultUnit = this.defaultUnit;
    }

    if (isEmptyObject(json)) {
      this.internals.setFormValue(null);
    } else {
      this.internals.setFormValue(JSON.stringify(json));
    }
  }

  #updateFormValidity() {
    const kindInput = this.#queryKindInput();
    const defaultUnitInput = this.#queryDefaultUnitInput();

    if (!kindInput.checkValidity()) {
      this.internals.setValidity(
        kindInput.validity,
        kindInput.validationMessage,
        kindInput.validationTarget,
      );
      return;
    }
    if (!defaultUnitInput.checkValidity()) {
      this.internals.setValidity(
        defaultUnitInput.validity,
        defaultUnitInput.validationMessage,
        defaultUnitInput.validationTarget,
      );
      return;
    }
    this.internals.setValidity({});
  }
}

customElements.define(WmMetricDefinition.TAG_NAME, WmMetricDefinition);

declare global {
  interface HTMLElementTagNameMap {
    [WmMetricDefinition.TAG_NAME]: WmMetricDefinition;
  }
}
